import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FieldProps } from '@rjsf/utils';

import { InfoBannerUiOptions } from './schema';

interface InfoBannerProps {
  severity?: InfoBannerUiOptions['severity'];
  title?: string;
  description?: string;
  linkText?: string;
  linkUrl?: string;
}

/**
 * Pure UI component — can be embedded inside other React components.
 * @param props InfoBannerProps
 * @returns InfoBanner React Element
 */
export const InfoBanner = ({
  severity = 'info',
  title,
  description,
  linkText,
  linkUrl,
}: InfoBannerProps) => {
  if (!title && !description) return null;

  const hasLink = !!linkText && !!linkUrl;

  return (
    <Alert
      severity={severity}
      action={
        hasLink ? (
          <Button
            component="a"
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" color={`${severity}`} />}
            sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
          >
            {linkText}
          </Button>
        ) : undefined
      }
      sx={{ alignItems: 'flex-start' }}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {description}
    </Alert>
  );
};

/**
 * Backstage Scaffolder Custom Field Extension — use directly in a template.yaml.
 * Reads all options from ui:options in the YAML.
 * @param props FieldProps injected by the Scaffolder
 * @returns InfoBanner React Element
 */
export const InfoBannerField = ({ uiSchema }: FieldProps<string>) => {
  const opts = (uiSchema as any)?.['ui:options'] as
    | InfoBannerUiOptions
    | undefined;

  return (
    <InfoBanner
      severity={opts?.severity}
      title={opts?.title}
      description={opts?.description}
      linkText={opts?.linkText}
      linkUrl={opts?.linkUrl}
    />
  );
};
